import { db, DEFAULT_BACKUP_URL } from '../db/database';
import { getActiveSessionToken, triggerAuthRequired, AuthError, refreshActiveSessionExpiry, validateSession } from './authService';

const TIMEOUT_MS = 15000;
let activeBackupPromise = null;
let queuedBackupSettings = null;

function resolveBackupUrl(settings) {
  return settings?.googleBackupUrl || DEFAULT_BACKUP_URL;
}

const EXPLICIT_AUTH_FAILURE_CODES = new Set([
  'AUTH_REQUIRED',
  'SESSION_INVALID',
  'SESSION_EXPIRED',
  'SESSION_REVOKED',
  'AUTH_NOT_CONFIGURED',
]);

function isAuthErrorResponse(data) {
  if (!data || typeof data !== 'object') return false;
  if (data.authRequired === true) return true;
  if (data.code && EXPLICIT_AUTH_FAILURE_CODES.has(data.code)) return true;
  return false;
}

async function handleHttp401(requestUrl) {
  const token = getActiveSessionToken();
  if (!token) {
    throw new AuthError('Authentication required. Please log in again.', 'AUTH_REQUIRED', true);
  }

  try {
    const validationUrl = requestUrl.split('?')[0];
    const validation = await validateSession({ googleBackupUrl: validationUrl });
    if (validation.valid) {
      throw new AuthError('The request was rejected by the cloud service, but your session is still valid.', 'REQUEST_UNAUTHORIZED', false, 401);
    }
    triggerAuthRequired();
    throw new AuthError('Session expired or is no longer valid. Please log in again.', 'SESSION_INVALID', true, 401);
  } catch (validationErr) {
    if (validationErr instanceof AuthError) {
      throw validationErr;
    }
    throw new AuthError('The request could not be verified. Your session has been preserved.', 'REQUEST_UNAUTHORIZED', false, 401);
  }
}

async function requestJson(url, options = {}) {
  const externalSignal = options.signal;
  const controller = new AbortController();
  const internalSignal = controller.signal;
  let abortReason = 'timeout';
  let onExternalAbort = null;

  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort();
      abortReason = 'external';
    } else {
      onExternalAbort = () => {
        abortReason = 'external';
        controller.abort();
      };
      externalSignal.addEventListener('abort', onExternalAbort);
    }
  }

  const actualTimeout = (typeof options.timeoutMs === 'number' && isFinite(options.timeoutMs) && options.timeoutMs > 0)
    ? options.timeoutMs
    : TIMEOUT_MS;
  const timeoutId = setTimeout(() => {
    abortReason = 'timeout';
    controller.abort();
  }, actualTimeout);

  try {
    const token = getActiveSessionToken();
    let effectiveUrl = url;
    if (token && (!options.method || options.method === 'GET')) {
      const separator = url.includes('?') ? '&' : '?';
      effectiveUrl = `${url}${separator}sessionToken=${encodeURIComponent(token)}`;
    }

    let effectiveBody = options.body;
    if (token && options.body && options.method === 'POST') {
      try {
        const bodyObj = JSON.parse(options.body);
        if (!bodyObj.sessionToken) {
          bodyObj.sessionToken = token;
          effectiveBody = JSON.stringify(bodyObj);
        }
      } catch { /* body not JSON — skip token attachment */ }
    }

    const fetchOptions = {
      method: options.method || 'GET',
      signal: internalSignal,
    };
    if (options.headers) fetchOptions.headers = options.headers;
    if (effectiveBody) fetchOptions.body = effectiveBody;

    const response = await fetch(effectiveUrl, fetchOptions);

    if (response.status === 401) {
      await handleHttp401(url);
    }
    if (!response.ok) {
      throw new Error(`Server returned ${response.status} ${response.statusText}`);
    }

    const contentType = response.headers.get('content-type') || '';
    let data;
    if (contentType.includes('application/json') || contentType.includes('text/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      if (!text || text.trim().length === 0) {
        throw new Error('Server returned an empty response. Check your Apps Script deployment.');
      }
      if (text.trim().startsWith('<') || text.trim().startsWith('<!')) {
        throw new Error('Server returned HTML instead of JSON. Check your Apps Script URL and deployment status.');
      }
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error('Server returned invalid JSON. Check your Apps Script deployment.');
      }
    }

    if (isAuthErrorResponse(data)) {
      triggerAuthRequired();
      throw new AuthError(data.error || data.message || 'Authentication required. Please log in again.', data.code, true, data.providerHttpStatus);
    }

    const validated = validateResponse(data);

    const serverExpiry = data.sessionExpiresAt || data.expiresAt;
    if (serverExpiry) {
      refreshActiveSessionExpiry(serverExpiry);
    }

    return validated;
  } catch (err) {
    if (err.name === 'AbortError') {
      if (abortReason === 'external') {
        throw new Error('Request was cancelled.');
      }
      throw new Error(`Request timed out after ${Math.round(actualTimeout / 1000)} seconds. Check your network or Apps Script URL.`);
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
    if (onExternalAbort && externalSignal) {
      externalSignal.removeEventListener('abort', onExternalAbort);
    }
  }
}

function validateResponse(data) {
  if (data && typeof data === 'object') {
    if (data.success === false) {
      const err = new Error(data.error || data.message || 'Cloud endpoint reported failure');
      if (data.code) err.code = data.code;
      if (data.authRequired) err.authRequired = data.authRequired;
      if (data.providerHttpStatus) err.providerHttpStatus = data.providerHttpStatus;
      throw err;
    }
    if (data.error && data.success !== true) {
      const err = new Error(data.error || 'Cloud endpoint returned an error');
      if (data.code) err.code = data.code;
      if (data.authRequired) err.authRequired = data.authRequired;
      if (data.providerHttpStatus) err.providerHttpStatus = data.providerHttpStatus;
      throw err;
    }
    if (data.failure === true) {
      throw new Error(data.message || data.error || 'Cloud endpoint reported failure');
    }
  }
  return data;
}

async function testCloudConnection(settings) {
  const url = resolveBackupUrl(settings);
  const healthUrl = `${url}?action=health`;
  const data = await requestJson(healthUrl);
  const isHealthy = data && (data.status === 'ok' || data.success === true || data.healthy === true || data.ok === true || data.data?.success === true || data.data?.status === 'ok');
  if (isHealthy) {
    return { connected: true, message: 'Connection successful' };
  }
  throw new Error('Health endpoint did not confirm service availability. Response lacked an explicit success indicator.');
}

async function listBackups(settings, limit = 10, options = {}) {
  const url = resolveBackupUrl(settings);
  const listUrl = `${url}?action=listBackups&limit=${limit}`;
  const data = await requestJson(listUrl, options);
  const backups = data?.backups || data?.data?.backups || [];
  return backups;
}

async function getLatestNonEmptyBackup(settings, options = {}) {
  const backups = await listBackups(settings, 10, options);
  if (!backups || backups.length === 0) return null;
  const hasProposalCountField = backups.some((b) => b.proposalCount !== undefined);
  if (hasProposalCountField) {
    const nonEmpty = backups.find((b) => (b.proposalCount || 0) > 0);
    if (!nonEmpty) return null;
    return nonEmpty;
  }
  return backups[0];
}

async function getBackupPayload(settings, backupId, options = {}) {
  if (!backupId) return null;
  const url = resolveBackupUrl(settings);
  const backupUrl = `${url}?action=getBackup&backupId=${encodeURIComponent(backupId)}`;
  const data = await requestJson(backupUrl, options);
  return data?.backup?.payload || data?.payload || data?.data?.backup?.payload || null;
}

function proposalContentScore(proposal) {
  if (!proposal || typeof proposal !== 'object' || proposal.__deleted === true) return 0;
  let score = 0;
  const textFields = ['title', 'name', 'clientName', 'clientCompany', 'description', 'executiveSummary', 'scope', 'inScope', 'outOfScope', 'assumptions', 'deliverables', 'termsAndConditions'];
  for (const key of textFields) {
    const value = proposal[key];
    if (typeof value === 'string') score += value.replace(/<[^>]*>/g, '').trim().length;
  }
  for (const key of ['sections', 'deliverables', 'milestones', 'paymentSchedule', 'financials']) {
    const value = proposal[key];
    if (Array.isArray(value)) score += value.length * 250;
    else if (value && typeof value === 'object') score += Object.keys(value).length * 100;
  }
  if (proposal.documentNumber) score += 100;
  if (proposal.version) score += 100;
  if (proposal.approval?.status === 'APPROVED') score += 500;
  return score;
}

function normaliseDeletedProposalIds(...sources) {
  const ids = new Set();
  sources.forEach((source) => {
    if (!Array.isArray(source)) return;
    source.forEach((value) => {
      const id = String(value || '').trim();
      if (id) ids.add(id);
    });
  });
  return Array.from(ids);
}

function extractProposalTombstones(proposals) {
  if (!Array.isArray(proposals)) return [];
  return proposals
    .filter((proposal) => proposal?.__deleted === true && proposal?.id)
    .map((proposal) => String(proposal.id).trim())
    .filter(Boolean);
}

function mergeProtectedProposals(localProposals, cloudProposals, deletedProposalIds = []) {
  const deleted = new Set(normaliseDeletedProposalIds(deletedProposalIds, extractProposalTombstones(cloudProposals)));
  const local = (Array.isArray(localProposals) ? localProposals : [])
    .filter((proposal) => proposal?.id && proposal.__deleted !== true && !deleted.has(String(proposal.id)));
  const cloud = (Array.isArray(cloudProposals) ? cloudProposals : [])
    .filter((proposal) => proposal?.id && proposal.__deleted !== true && !deleted.has(String(proposal.id)));

  const localById = new Map(local.map((proposal) => [String(proposal.id), proposal]));
  const cloudById = new Map(cloud.map((proposal) => [String(proposal.id), proposal]));
  const allIds = new Set([...localById.keys(), ...cloudById.keys()]);
  const merged = [];

  allIds.forEach((id) => {
    if (deleted.has(id)) return;
    const localProposal = localById.get(id);
    const cloudProposal = cloudById.get(id);

    // Preserve proposals that exist on only one side. This prevents a partially
    // hydrated browser from silently dropping a cloud-only SOW during sync.
    if (!localProposal) {
      if (cloudProposal) merged.push(cloudProposal);
      return;
    }
    if (!cloudProposal) {
      merged.push(localProposal);
      return;
    }

    const localScore = proposalContentScore(localProposal);
    const cloudScore = proposalContentScore(cloudProposal);

    // Never allow a materially richer cloud proposal to be overwritten by a
    // degraded local copy. This protects against IndexedDB resets, partial
    // hydration and origin-specific browser storage being synced upstream.
    if (cloudScore >= Math.max(500, localScore * 1.5) && cloudScore > localScore + 500) {
      merged.push(cloudProposal);
      return;
    }
    merged.push(localProposal);
  });

  return merged;
}

function buildBackupProposalSnapshot(proposals, deletedProposalIds) {
  const deleted = normaliseDeletedProposalIds(deletedProposalIds);
  const deletedSet = new Set(deleted);
  const live = (Array.isArray(proposals) ? proposals : [])
    .filter((proposal) => proposal?.id && proposal.__deleted !== true && !deletedSet.has(String(proposal.id)));

  // Tombstones are intentionally tiny proposal-shaped records. Keeping them in
  // the proposal snapshot means an intentional deletion remains non-empty in
  // backup metadata and cannot be mistaken for a damaged empty workspace.
  return live.concat(deleted.map((id) => ({ id, __deleted: true })));
}

async function reconcileLocalWorkspaceWithCloud(settings, options = {}) {
  const targetBackup = await getLatestNonEmptyBackup(settings, options);
  if (!targetBackup) return { reconciled: false, reason: 'no_backup' };

  const payload = await getBackupPayload(settings, targetBackup.backupId || targetBackup.id, options);
  if (!payload || !Array.isArray(payload.proposals)) return { reconciled: false, reason: 'no_proposals' };

  const localProposals = await db.proposals.toArray();
  const localSettings = await db.settings.get('global');
  const cloudDeletedProposalIds = normaliseDeletedProposalIds(
    extractProposalTombstones(payload.proposals),
    payload.settings?.deletedProposalIds
  );
  const pendingLocalDeletedProposalIds = normaliseDeletedProposalIds(localSettings?.deletedProposalIds);
  const deletedProposalIds = normaliseDeletedProposalIds(
    cloudDeletedProposalIds,
    pendingLocalDeletedProposalIds
  );
  const merged = mergeProtectedProposals(localProposals, payload.proposals, deletedProposalIds);
  const localById = new Map(localProposals.filter((proposal) => proposal?.id).map((proposal) => [String(proposal.id), proposal]));
  const deletedSet = new Set(deletedProposalIds);
  const proposalChanged = merged.length !== localProposals.filter((proposal) => proposal?.id && !deletedSet.has(String(proposal.id))).length ||
    merged.some((proposal) => JSON.stringify(localById.get(String(proposal.id))) !== JSON.stringify(proposal)) ||
    localProposals.some((proposal) => proposal?.id && deletedSet.has(String(proposal.id)));

  const cloudDeletedSet = new Set(cloudDeletedProposalIds);
  const stillPendingLocalDeletes = pendingLocalDeletedProposalIds.filter((id) => !cloudDeletedSet.has(id));
  const deletionMarkerChanged = JSON.stringify(stillPendingLocalDeletes) !== JSON.stringify(pendingLocalDeletedProposalIds);

  if (!proposalChanged && !deletionMarkerChanged) {
    return { reconciled: false, reason: 'already_current' };
  }

  await db.transaction('rw', db.proposals, db.settings, async () => {
    for (const id of deletedProposalIds) {
      await db.proposals.delete(id);
    }
    for (const proposal of merged) {
      const localProposal = localById.get(String(proposal.id));
      if (!localProposal || JSON.stringify(localProposal) !== JSON.stringify(proposal)) {
        await db.proposals.put(proposal);
      }
    }
    if (deletionMarkerChanged && localSettings) {
      await db.settings.update('global', { deletedProposalIds: stillPendingLocalDeletes });
    }
  });

  return {
    reconciled: true,
    proposalCount: merged.length,
    restoredCloudOnlyCount: merged.filter((proposal) => !localById.has(String(proposal.id))).length,
  };
}

async function restoreLatestBackup(settings, options = {}) {
  const signal = options.signal;
  if (signal?.aborted) throw new Error('Restore cancelled before starting.');

  const targetBackup = await getLatestNonEmptyBackup(settings, options);
  if (!targetBackup) return { restored: false, reason: 'no_backup' };

  if (signal?.aborted) throw new Error('Restore cancelled after listing backups.');

  const cloudPayload = await getBackupPayload(settings, targetBackup.backupId || targetBackup.id, options);
  if (!cloudPayload) throw new Error('Cloud backup contains no valid payload');

  const { proposals, snippets, settings: cloudSettings } = cloudPayload;
  if (proposals !== undefined && !Array.isArray(proposals)) throw new Error('Cloud backup proposals data is invalid (not an array)');
  if (snippets !== undefined && !Array.isArray(snippets)) throw new Error('Cloud backup snippets data is invalid (not an array)');
  if (cloudSettings !== undefined && typeof cloudSettings !== 'object') throw new Error('Cloud backup settings data is invalid (not an object)');

  const deletedProposalIds = normaliseDeletedProposalIds(
    extractProposalTombstones(proposals),
    cloudSettings?.deletedProposalIds
  );
  const deletedSet = new Set(deletedProposalIds);
  const liveProposals = (Array.isArray(proposals) ? proposals : [])
    .filter((proposal) => proposal?.id && proposal.__deleted !== true && !deletedSet.has(String(proposal.id)));

  if (signal?.aborted) throw new Error('Restore cancelled before database transaction.');

  await db.transaction('rw', db.proposals, db.snippets, db.settings, async () => {
    if (signal?.aborted) throw new Error('Restore cancelled: transaction aborted before clearing tables.');
    await db.proposals.clear();
    if (signal?.aborted) throw new Error('Restore cancelled: transaction aborted after clearing proposals.');
    await db.snippets.clear();
    if (signal?.aborted) throw new Error('Restore cancelled: transaction aborted after clearing snippets.');
    if (liveProposals.length > 0) await db.proposals.bulkPut(liveProposals);
    if (signal?.aborted) throw new Error('Restore cancelled: transaction aborted after inserting proposals.');
    if (snippets?.length > 0) await db.snippets.bulkPut(snippets);
    if (cloudSettings) {
      const restoredSettings = { ...cloudSettings, id: 'global' };
      delete restoredSettings.deletedProposalIds;
      await db.settings.put(restoredSettings);
    }
    if (signal?.aborted) throw new Error('Restore cancelled: transaction aborted before completion.');
  });

  return { restored: true, proposalCount: liveProposals.length, snippetCount: snippets?.length || 0 };
}

async function performWorkspaceBackup(settings) {
  const url = resolveBackupUrl(settings);
  let proposals = await db.proposals.toArray();
  const snippets = await db.snippets.toArray();
  const currentSettings = await db.settings.get('global');
  const pendingLocalDeletedProposalIds = normaliseDeletedProposalIds(currentSettings?.deletedProposalIds);
  let deletedProposalIds = pendingLocalDeletedProposalIds;

  // A backup write is allowed only after the latest cloud snapshot has been
  // read successfully. If the protection read is unavailable, preserve the
  // local workspace and defer the upload rather than risk overwriting a richer
  // cloud SOW with degraded or partially hydrated browser data.
  const targetBackup = await getLatestNonEmptyBackup(settings);
  if (targetBackup) {
    const cloudPayload = await getBackupPayload(settings, targetBackup.backupId || targetBackup.id);
    if (!cloudPayload || !Array.isArray(cloudPayload.proposals)) {
      throw new Error('Cloud protection check could not verify the latest proposal snapshot. Sync was deferred safely.');
    }
    deletedProposalIds = normaliseDeletedProposalIds(
      deletedProposalIds,
      extractProposalTombstones(cloudPayload.proposals),
      cloudPayload.settings?.deletedProposalIds
    );
    proposals = mergeProtectedProposals(proposals, cloudPayload.proposals, deletedProposalIds);
  }

  const proposalSnapshot = buildBackupProposalSnapshot(proposals, deletedProposalIds);
  const settingsSnapshot = { ...(currentSettings || {}) };
  delete settingsSnapshot.deletedProposalIds;

  const payload = {
    action: 'saveBackup',
    workspaceName: 'Default Workspace',
    deviceLabel: 'ScopeShield Frontend',
    appVersion: '1.0.0',
    payload: { proposals: proposalSnapshot, snippets, settings: settingsSnapshot },
  };
  const data = await requestJson(url, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    body: JSON.stringify(payload),
  });
  const isSuccess = data && (data.success === true || data.status === 'ok' || data.saved === true || data.data?.success === true || data.data?.saved === true);
  if (isSuccess) {
    if (pendingLocalDeletedProposalIds.length > 0) {
      await db.settings.update('global', { deletedProposalIds: [] });
    }
    return { success: true };
  }
  throw new Error('Cloud backup was not explicitly confirmed by the server. Response lacked a recognised success indicator.');
}

async function pushWorkspaceBackup(settings) {
  // Every request made while a backup is already running queues exactly one
  // follow-up pass. This prevents a publish/delete/save that occurs mid-sync
  // from being acknowledged by an older in-flight snapshot.
  if (activeBackupPromise) {
    queuedBackupSettings = settings || queuedBackupSettings;
    return activeBackupPromise;
  }

  activeBackupPromise = (async () => {
    let nextSettings = settings;
    let result;
    do {
      queuedBackupSettings = null;
      result = await performWorkspaceBackup(nextSettings);
      nextSettings = queuedBackupSettings;
    } while (nextSettings);
    return result;
  })();

  try {
    return await activeBackupPromise;
  } finally {
    activeBackupPromise = null;
    queuedBackupSettings = null;
  }
}

async function deleteProposalSafely(proposalId, settings) {
  const id = String(proposalId || '').trim();
  if (!id) throw new Error('A proposal id is required for deletion.');

  await db.transaction('rw', db.proposals, db.settings, async () => {
    const currentSettings = await db.settings.get('global') || { id: 'global' };
    const deletedProposalIds = normaliseDeletedProposalIds(currentSettings.deletedProposalIds, [id]);
    await db.settings.put({ ...currentSettings, id: 'global', deletedProposalIds });
    await db.proposals.delete(id);
  });

  if (!settings?.googleBackupConnected || !resolveBackupUrl(settings)) {
    return { deleted: true, cloudSyncPending: false };
  }

  try {
    await pushWorkspaceBackup(settings);
    return { deleted: true, cloudSyncPending: false };
  } catch (err) {
    // The local tombstone remains in IndexedDB so the next successful sync can
    // propagate the deletion without re-introducing the cloud copy.
    return { deleted: true, cloudSyncPending: true, error: err };
  }
}

export {
  resolveBackupUrl,
  requestJson,
  testCloudConnection,
  listBackups,
  getLatestNonEmptyBackup,
  getBackupPayload,
  reconcileLocalWorkspaceWithCloud,
  restoreLatestBackup,
  pushWorkspaceBackup,
  deleteProposalSafely,
  TIMEOUT_MS,
};